import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";

// Mapping modali holati: WinPro (productId, textureId) → akfaProductId, N:1.
// orig — serverdagi holat (yuklangan qatorlar bo'yicha), edits — faqat orig dan farq qiladigan qiymatlar.
// draft[key] = key in edits ? edits[key] : orig[key]

export const textureKey = (productId, textureId) => `${productId}:${textureId}`;

export const idEq = (a, b) => (a == null ? b == null : b != null && String(a) === String(b));

const nz = (value) => (value === undefined ? null : value);

// Product rejimida Akfa qidiruvining boshlang'ich qiymati (FRONTEND_Plan_4, 3.0):
// 1) barcha (...) qismlar olib tashlanadi; 2) so'zlarga bo'linadi; 3) sof raqam bo'lmagan birinchi so'z
// "(13) (Prostoe Otkr) Petli Komplekt (FORNAX)" → "Petli", "152 Inja Chovush" → "Inja"
export const defaultAkfaQuery = (name) => {
    const tokens = (name || "")
        .replace(/\([^)]*\)/g, " ")
        .trim()
        .split(/[\s—-]+/)
        .filter(Boolean);
    return tokens.find((token) => !/^\d+$/.test(token)) || "";
};

export const akfaLabel = (akfa) =>
    akfa ? [akfa.externalId ?? (akfa.name ? null : `#${akfa.id}`), akfa.name].filter(Boolean).join(" ") : "";

// GET /winpro-products/{productId}/texture-options elementi →
// { textureId, textureName, price, currency, mapped, mappedToAkfaProductId, mappedToAkfaName }
export const normalizeTextureOption = (option, product) => {
    const akfaId = nz(option.mappedToAkfaProductId);
    return {
        key: textureKey(product.productId, option.textureId),
        productId: product.productId,
        productName: product.productName || "",
        article: product.article || "",
        textureId: option.textureId,
        textureName: option.textureName || "",
        price: nz(option.price),
        currency: option.currencySymbol || option.currency || "",
        akfaProductId: akfaId,
        akfa: akfaId == null ? null : { id: akfaId, name: option.mappedToAkfaName || "" },
    };
};

// GET /mappings/akfa-products elementi — tovar + unga ulangan teksturalar (serverdagi saqlangan holat)
// { id, externalId, name, groupId, groupName, price, currency, unitName, linkedTextureCount, linkedTextures }
export const normalizeAkfaItem = (item) => ({
    id: item.id,
    externalId: item.externalId ?? null,
    name: item.name || "",
    groupId: item.groupId ?? null,
    groupName: item.groupName || "",
    price: nz(item.price),
    currency: item.currency || "",
    linked: (item.linkedTextures || []).map((row) => ({
        key: textureKey(row.productId, row.textureId),
        productId: row.productId,
        productName: row.productName || "",
        textureId: row.textureId,
        textureName: row.textureName || "",
    })),
});

// Akfa ma'lumotini birlashtirish: bo'sh maydonlar mavjud qiymatni o'chirmaydi
const mergeAkfa = (prev, next) => {
    const merged = { ...prev };
    Object.entries(next).forEach(([field, value]) => {
        if (value != null && value !== "") merged[field] = value;
    });
    return merged;
};

const metaOf = (row) => ({
    productId: row.productId,
    productName: row.productName,
    textureId: row.textureId,
    textureName: row.textureName,
});

const INITIAL = { orig: {}, edits: {}, meta: {}, akfa: {}, checked: {} };

const pruneEdits = (edits, orig) => {
    const next = {};
    Object.entries(edits).forEach(([key, value]) => {
        if (!idEq(value, nz(orig[key]))) next[key] = value;
    });
    return next;
};

function reducer(state, action) {
    switch (action.type) {
        case "reset":
            return INITIAL;
        // Tekstura qatorlari yuklandi: meta + akfa ma'lumoti; orig faqat yangi kalitlar uchun to'ldiriladi
        case "textures": {
            const orig = { ...state.orig };
            const meta = { ...state.meta };
            const akfa = { ...state.akfa };
            action.rows.forEach((row) => {
                meta[row.key] = metaOf(row);
                if (!(row.key in orig)) orig[row.key] = row.akfaProductId;
                if (row.akfa) akfa[row.akfa.id] = mergeAkfa(akfa[row.akfa.id], row.akfa);
            });
            return { ...state, orig, meta, akfa, edits: pruneEdits(state.edits, orig) };
        }
        // Akfa tovarlari yuklandi: ularga ulangan teksturalar ham orig ga kiradi (chip dagi × uchun expected qiymat)
        case "akfaItems": {
            const orig = { ...state.orig };
            const meta = { ...state.meta };
            const akfa = { ...state.akfa };
            action.items.forEach((item) => {
                akfa[item.id] = mergeAkfa(akfa[item.id], { id: item.id, externalId: item.externalId, name: item.name });
                item.linked.forEach((row) => {
                    meta[row.key] = { ...meta[row.key], ...metaOf(row) };
                    if (!(row.key in orig)) orig[row.key] = item.id;
                });
            });
            return { ...state, orig, meta, akfa, edits: pruneEdits(state.edits, orig) };
        }
        case "assign": {
            const edits = { ...state.edits };
            action.keys.forEach((key) => {
                edits[key] = action.akfaId;
            });
            return { ...state, edits: pruneEdits(edits, state.orig), checked: {} };
        }
        case "unlink": {
            const edits = pruneEdits({ ...state.edits, [action.key]: null }, state.orig);
            return { ...state, edits };
        }
        case "toggleCheck":
            return { ...state, checked: { ...state.checked, [action.key]: !state.checked[action.key] } };
        // 409 dan keyin: orig = server; to'qnashgan qatorlar server qiymatiga qaytadi, qolgan o'zgarishlar saqlanadi
        case "serverState": {
            const orig = { ...state.orig };
            const meta = { ...state.meta };
            const akfa = { ...state.akfa };
            action.rows.forEach((row) => {
                orig[row.key] = row.akfaProductId;
                meta[row.key] = metaOf(row);
                if (row.akfa) akfa[row.akfa.id] = mergeAkfa(akfa[row.akfa.id], row.akfa);
            });
            const edits = { ...state.edits };
            action.conflictKeys.forEach((key) => delete edits[key]);
            return { ...state, orig, meta, akfa, edits: pruneEdits(edits, orig) };
        }
        default:
            return state;
    }
}

export default function useMappingDraft() {
    const [state, dispatch] = useReducer(reducer, INITIAL);

    const valueOf = useCallback(
        (key) => (key in state.edits ? state.edits[key] : nz(state.orig[key])),
        [state.edits, state.orig]
    );

    const checkedKeys = useMemo(
        () => Object.keys(state.checked).filter((key) => state.checked[key]),
        [state.checked]
    );

    const changeKeys = Object.keys(state.edits);

    const toPayload = () =>
        changeKeys.map((key) => {
            const meta = state.meta[key] || {};
            return {
                productId: meta.productId,
                textureId: meta.textureId,
                akfaProductId: nz(state.edits[key]),
                expectedAkfaProductId: nz(state.orig[key]),
            };
        });

    // Sudralgan/tashlangan tekstura belgilangan bo'lsa — barcha belgilanganlar birga ketadi
    const keysWithChecked = (key) => (state.checked[key] ? checkedKeys : [key]);

    return {
        ...state,
        dispatch,
        valueOf,
        checkedKeys,
        changeKeys,
        changeCount: changeKeys.length,
        toPayload,
        keysWithChecked,
    };
}

export function useDebouncedValue(value, delay = 300) {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const timer = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(timer);
    }, [value, delay]);
    return debounced;
}

// Server sahifalash + infinite scroll. fetchPage(pageIndex) → { success, data: HubPageResponse }
export function usePagedList(fetchPage, deps, { enabled = true, size = 50, onItems } = {}) {
    const [state, setState] = useState({ items: [], total: 0, page: -1, loading: false, done: false });
    const requestId = useRef(0);
    const onItemsRef = useRef(onItems);
    onItemsRef.current = onItems;

    const load = useCallback(
        async (page, replace) => {
            const id = ++requestId.current;
            setState((prev) => ({ ...prev, loading: true, ...(replace ? { items: [], page: -1, done: false } : {}) }));
            const response = await fetchPage(page);
            if (id !== requestId.current) return;
            if (!response.success) {
                setState((prev) => ({ ...prev, loading: false, done: true, error: response }));
                return;
            }
            const items = response.data?.data || (Array.isArray(response.data) ? response.data : []);
            const total = response.data?.total ?? items.length;
            onItemsRef.current?.(items);
            setState((prev) => {
                const merged = replace ? items : [...prev.items, ...items];
                return {
                    items: merged,
                    total,
                    page,
                    loading: false,
                    // hasMore — elementlar soni sahifa hajmiga mos kelmaydigan holatlar uchun (global teksturalar)
                    done:
                        response.data?.hasMore != null
                            ? !response.data.hasMore
                            : items.length < size || merged.length >= total,
                };
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        deps
    );

    useEffect(() => {
        if (enabled) load(0, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [load, enabled]);

    const loadMore = () => {
        if (!state.loading && !state.done) load(state.page + 1, false);
    };

    return { ...state, loadMore, reload: () => load(0, true) };
}
